import React, {
  memo, useEffect, useRef, useState,
} from 'react'

import ReactTooltip from 'react-tooltip'

import {
  Icon, Button, Close, MoreFilters as MoreFiltersIcon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, func, string } from 'prop-types'
import { createPortal } from 'react-dom'

import AdvancedFiltersContent from 'Pods/SearchPage/Shared/AdvancedFiltersContent/AdvancedFiltersContent'
import DatePublishedContent from 'Pods/SearchPage/Shared/DatePublishedContent/DatePublishedContent'
import DesignerContent from 'Pods/SearchPage/Shared/DesignerContent/DesignerContent'
import { FEATURES, PRINT_FEATURES } from 'Pods/SearchPage/Shared/FeaturesContent/constants'
import FeaturesContent from 'Pods/SearchPage/Shared/FeaturesContent/FeaturesContent'
import FileFormatContent from 'Pods/SearchPage/Shared/FileFormatContent/FileFormatContent'
import LicenseContent from 'Pods/SearchPage/Shared/LicenseContent/LicenseContent'
import PolyCountContent from 'Pods/SearchPage/Shared/PolyCountContent/PolyCountContent'
import PriceContent from 'Pods/SearchPage/Shared/PriceContent/PriceContent'
import QualityContent from 'Pods/SearchPage/Shared/QualityContent/QualityContent'
import SizeContent from 'Pods/SearchPage/Shared/SizeContent/SizeContent'
import StyleContent from 'Pods/SearchPage/Shared/StyleContent/StyleContent'
import TagsContent from 'Pods/SearchPage/Shared/TagsContent/TagsContent'
import { EVENT_SOURCE } from 'Pods/SearchPage/Shared/Tracking/constants'
import { FilterTrackingProvider } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import { trackResetClick } from 'Pods/SearchPage/Shared/Tracking/searchPageTracker'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { ASSET_TYPE } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import CollapsibleSection from './Components/CollapsibleSection/CollapsibleSection'

const headerIcon = <Icon notSpaced svgComponent={MoreFiltersIcon} color="darker" size="small" />
const closeIcon = (
  <Icon notSpaced svgComponent={Close} size="small" color="dark" className="more-filters__drawer-close-icon" />
)

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

// Modal starts where the fixed app navbar ends; measured on mount so it adapts to the
// sales-off bar and breakpoint-specific navbar heights.
const getNavbarOffset = () => {
  const nav = typeof document !== 'undefined' && document.querySelector('.wayfinder')
  return nav ? Math.round(nav.getBoundingClientRect().bottom) : 0
}

function MoreFiltersDrawer({ onClose, onReset, fileTypes, onFileTypeChange }) {
  const translate = useTranslate()

  const drawerRef = useRef(null)
  const [navOffset] = useState(getNavbarOffset)
  const is3DOnly = useSearchPageStore(state => state.assetType === ASSET_TYPE.THREE_D_ONLY)
  const showStyle = useSearchPageStore(state => state.assetType !== ASSET_TYPE.ALL)
  const is3dPrintOnly = useSearchPageStore(state => state.assetType === ASSET_TYPE.THREE_D_PRINT_ONLY)
  const pageConfig = usePageConfig()
  // Size has no backend filter yet (CU-86cb1n0jp), so it stays behind the flag - and hidden while
  // pageConfig is still loading, which matches the flag's default.
  const showSize = is3dPrintOnly && Boolean(pageConfig?.previewPageSizeFields)
  // Tags/categories are secondary-bar filters, so the section follows the bar's visibility rule.
  const showCategories = useSearchPageStore(state => state.assetType !== ASSET_TYPE.ALL)

  // Lock background page scroll while open, reusing the app-wide body class. The drawer's own
  // class hides the chat launcher (see search_page/_chatbot.scss); `:has()` would do it without JS,
  // but it is missing from the browserslist floors.
  useEffect(() => {
    document.body.classList.add('disable-page-scroll', 'more-filters-drawer-open')
    return () => document.body.classList.remove('disable-page-scroll', 'more-filters-drawer-open')
  }, [])

  // Close when the main mobile menu opens.
  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (event.target.closest?.('.hamburger')) {
        onClose()
      }
    }
    document.addEventListener('click', handleDocumentClick)

    return () => document.removeEventListener('click', handleDocumentClick)
  }, [onClose])

  useEffect(() => {
    const drawer = drawerRef.current
    const previouslyFocused = document.activeElement
    const getFocusable = () => (drawer ? Array.from(drawer.querySelectorAll(FOCUSABLE_SELECTOR)) : [])

    const focusable = getFocusable()
    if (focusable.length > 0) {
      focusable[0].focus()
    } else if (drawer) {
      drawer.focus()
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const items = getFocusable()
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus()
      }
    }
  }, [onClose])

  // react-tooltip only auto-hides on window scroll; the drawer is its own scroll container,
  // so dismiss any open tooltip when its content scrolls.
  const handleScroll = () => {
    ReactTooltip.hide()
  }

  const handleReset = () => {
    trackResetClick({ eventSource: EVENT_SOURCE.MOBILE_MENU })
    onReset()
  }

  return createPortal(
    // Portaled to body so the full-screen modal escapes the filter bar's stacking context
    // (.search-page__main-filters { z-index: 2 }). It starts below the fixed navbar via the
    // measured offset, leaving the navbar visible.
    <FilterTrackingProvider eventSource={EVENT_SOURCE.MOBILE_MENU}>
      {/* Only shown from md, where the drawer is a side sheet; a click on the dimmed page closes it. */}
      <div className="more-filters__drawer-backdrop" style={{ top: navOffset }} onClick={onClose} aria-hidden="true" />
      <div
        ref={drawerRef}
        className="more-filters__drawer"
        role="dialog"
        aria-modal="true"
        aria-label={translate('search_page.filters.more_filters.modal_title')}
        tabIndex={-1}
        style={{ top: navOffset }}
      >
        {/* Header + sections scroll together; the footer stays pinned at the bottom (see SCSS). */}
        <div className="more-filters__drawer-scroll" onScroll={handleScroll}>
          <div className="more-filters__drawer-header">
            <div className="more-filters__drawer-title cgt-overline-md">
              {headerIcon}
              <span>{translate('search_page.filters.more_filters.modal_title')}</span>
            </div>
            <Button
              type="ghost"
              variant="only-icon"
              icon={closeIcon}
              customClass="more-filters__drawer-close"
              onClick={onClose}
              aria-label={translate('search_page.filters.more_filters.close_label')}
            />
          </div>
          {/* Figma 590:69793 opens only the first two groups; the rest start collapsed. */}
          <CollapsibleSection title={translate('search_page.filters.price.title')}>
            <PriceContent />
          </CollapsibleSection>
          <CollapsibleSection title={translate('search_page.filters.file_format.title')}>
            <FileFormatContent
              fileTypes={fileTypes}
              onFilterChange={onFileTypeChange}
              limit={3}
              hideShowMore={false}
            />
          </CollapsibleSection>
          <CollapsibleSection title={translate('search_page.filters.quality.title')} defaultOpen={false}>
            <QualityContent />
          </CollapsibleSection>
          <CollapsibleSection title={translate('search_page.filters.license.title')} defaultOpen={false}>
            <LicenseContent />
          </CollapsibleSection>
          <CollapsibleSection title={translate('search_page.filters.designers.title')} defaultOpen={false}>
            {/* The drawer's footer Reset clears designers, so the section hides its own. */}
            <DesignerContent hideReset />
          </CollapsibleSection>
          <CollapsibleSection title={translate('search_page.filters.date_published.title')} defaultOpen={false}>
            <DatePublishedContent />
          </CollapsibleSection>
          {/* Features consolidates each asset type's own secondary-bar toggles, so the section
              appears for 3D and 3D-print selections with their respective lists. */}
          {(is3DOnly || is3dPrintOnly) && (
            <CollapsibleSection title={translate('search_page.filters.features.title')} defaultOpen={false}>
              <FeaturesContent features={is3dPrintOnly ? PRINT_FEATURES : FEATURES} hideReset />
            </CollapsibleSection>
          )}
          {showStyle && (
            <CollapsibleSection title={translate('search_page.filters.style.title')} defaultOpen={false}>
              <StyleContent hideReset collapsible />
            </CollapsibleSection>
          )}
          {/* Figma 2807:156062 moves the info tooltip from the Low-poly option to the section title. */}
          {is3DOnly && (
            <CollapsibleSection
              title={translate('search_page.filters.poly_count.title')}
              tooltip={translate('search_page.filters.poly_count.info_tooltip')}
              defaultOpen={false}
            >
              <PolyCountContent hideReset hideTooltip />
            </CollapsibleSection>
          )}
          {/* Size is a 3D-print-only secondary filter, so the section only appears for that asset type. */}
          {showSize && (
            <CollapsibleSection title={translate('search_page.filters.size.title')} defaultOpen={false}>
              <SizeContent hideReset />
            </CollapsibleSection>
          )}
          {showCategories && (
            <CollapsibleSection title={translate('search_page.filters.categories.title')} defaultOpen={false}>
              {/* The drawer's footer Reset clears tags, so the section hides its own. */}
              <TagsContent hideReset collapsible />
            </CollapsibleSection>
          )}
          {/* General + My Library share the content component with the desktop More Filters popover;
              the collapsible header ("Advanced filters") replaces the General label. */}
          <CollapsibleSection title={translate('search_page.filters.advanced.title')} defaultOpen={false}>
            <AdvancedFiltersContent hideGeneralLabel />
          </CollapsibleSection>
        </div>
        <div className="more-filters__drawer-footer">
          <Button
            type="secondary"
            customClass="more-filters__footer-button"
            onClick={handleReset}
          >
            {translate('search_page.filters.more_filters.reset')}
          </Button>
          <Button
            type="primary"
            customClass="more-filters__footer-button"
            onClick={onClose}
          >
            {translate('search_page.filters.more_filters.apply_filters')}
          </Button>
        </div>
      </div>
    </FilterTrackingProvider>,
    document.body,
  )
}

MoreFiltersDrawer.propTypes = {
  onClose: func.isRequired,
  onReset: func.isRequired,
  fileTypes: arrayOf(string).isRequired,
  onFileTypeChange: func.isRequired,
}

export default memo(MoreFiltersDrawer)
